import * as yup from 'yup';
import { StatusPill } from '../../component/common/status-pill/StatusPill';
import { RowActionsDropdown } from '../../component/common/table/action-dropdown/RowActionsDropdown';

const RULE_TYPE_OPTIONS = [
  { key: 'FIRST_ORDER', label: 'First Order Only', value: 'FIRST_ORDER', active: true },
  { key: 'MIN_ORDER_VALUE', label: 'Minimum Order Value', value: 'MIN_ORDER_VALUE', active: true },
  { key: 'CATEGORY', label: 'Specific Menu Category', value: 'CATEGORY', active: true }
];

const RULE_TYPE_LABELS = {
  FIRST_ORDER: 'First Order Only',
  MIN_ORDER_VALUE: 'Minimum Order Value',
  CATEGORY: 'Specific Menu Category'
};

export const getPromotionColumnDefinition = (onEdit, onView, onDelete, onViewLog) => [
  {
    id: 'description',
    accessorKey: 'description',
    header: 'Description',
    size: 220,
    minSize: 140,
    filterFn: 'tableFilterFn'
  },
  {
    id: 'discount',
    header: 'Discount',
    size: 100,
    minSize: 80,
    accessorFn: (row) => row.discount ?? 0,
    cell: ({ row }) => `${row.original.discount ?? 0}%`
  },
  {
    id: 'ruleType',
    accessorKey: 'ruleType',
    header: 'Rule',
    size: 170,
    minSize: 130,
    cell: ({ row }) => {
      const { ruleType, minOrderValue } = row.original;
      const label = RULE_TYPE_LABELS[ruleType] ?? ruleType;
      if (ruleType === 'MIN_ORDER_VALUE' && minOrderValue != null) {
        return `${label} (R${minOrderValue})`;
      }
      return label;
    }
  },
  {
    id: 'dateRange',
    header: 'Runs',
    size: 180,
    minSize: 140,
    cell: ({ row }) => {
      const { startsAt, endsAt } = row.original;
      if (!startsAt && !endsAt) return <span style={{ color: 'var(--text-gray)' }}>Always</span>;
      const from = startsAt ? new Date(startsAt).toLocaleDateString('en-ZA') : 'Now';
      const to = endsAt ? new Date(endsAt).toLocaleDateString('en-ZA') : 'Ongoing';
      return `${from} to ${to}`;
    }
  },
  {
    id: 'usage',
    header: 'Usage',
    size: 130,
    minSize: 100,
    accessorFn: (row) => row.usageCount ?? 0,
    cell: ({ row }) => (
      <span>
        {row.original.usageCount ?? 0} redeemed
        {row.original.usageLimit != null && (
          <span style={{ color: 'var(--text-gray)' }}> · {row.original.usageLimit}/customer</span>
        )}
      </span>
    )
  },
  {
    id: 'active',
    accessorKey: 'active',
    header: 'Status',
    size: 100,
    minSize: 80,
    cell: ({ row }) => (
      <StatusPill type={row.original.active ? 'active' : 'inactive'}>
        {row.original.active ? 'Active' : 'Inactive'}
      </StatusPill>
    )
  },
  {
    id: 'actions',
    header: '',
    size: 40,
    minSize: 40,
    maxSize: 40,
    enableSorting: false,
    disableExport: true,
    cell: ({ row }) => {
      const actions = [
        onViewLog && { label: 'View Log', value: 'log', onSelect: () => onViewLog(row.original) },
        onEdit && { label: 'Edit', value: 'edit', onSelect: () => onEdit(row.original) },
        onDelete && { label: 'Delete', value: 'delete', onSelect: () => onDelete(row.original) }
      ].filter(Boolean);
      return <RowActionsDropdown row={row} actions={actions} />;
    }
  }
];

export const PROMOTION_TABS = (categories = []) => {
  const categoryOptions = [
    { key: 'none', label: 'None', value: '', active: true },
    ...categories.map((c) => ({ key: c.id, label: c.name, value: c.id, active: true }))
  ];

  return [
  {
    label: 'Details',
    fields: [
      {
        type: 'textarea',
        name: 'description',
        label: 'Description (max 30 characters)',
        colSpan: 2,
        maxLength: 30,
        validation: yup.string().max(30, 'Max 30 characters').nullable().optional()
      },
      {
        type: 'decimal',
        name: 'discount',
        label: 'Discount (%)',
        required: true,
        validation: yup.number().required('Required')
      },
      {
        type: 'number',
        name: 'usageLimit',
        label: 'Usage Limit (per customer)',
        validation: yup.number().nullable().optional()
      },
      {
        type: 'select',
        name: 'ruleType',
        label: 'Eligibility Rule',
        defaultValue: 'FIRST_ORDER',
        options: RULE_TYPE_OPTIONS,
        validation: yup.string().required('Required')
      },
      {
        type: 'yesno',
        name: 'active',
        label: 'Active',
        defaultValue: true,
        validation: yup.boolean().optional()
      },
      {
        type: 'yesno',
        name: 'alwaysOn',
        label: 'Always On',
        defaultValue: true,
        validation: yup.boolean().optional()
      },
      {
        type: 'date',
        name: 'startsAt',
        label: 'Starts',
        visibleIf: (data) => !data.alwaysOn,
        validation: yup.string().nullable().optional()
      },
      {
        type: 'date',
        name: 'endsAt',
        label: 'Ends',
        visibleIf: (data) => !data.alwaysOn,
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'minOrderValue',
        label: 'Minimum Order Value (R)',
        colSpan: 2,
        visibleIf: (data) => data.ruleType === 'MIN_ORDER_VALUE',
        validation: yup.number().nullable().optional()
      },
      {
        type: 'select',
        name: 'categoryId',
        label: 'Category',
        colSpan: 2,
        options: categoryOptions,
        visibleIf: (data) => data.ruleType === 'CATEGORY',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'image',
        name: 'imageUrl',
        label: 'Banner Image',
        colSpan: 2,
        validation: yup.string().nullable().optional()
      },
      {
        type: 'input',
        name: 'color',
        label: 'Banner Color',
        validation: yup.string().nullable().optional()
      }
    ]
  }
  ];
};
